<template>
    <div class="email-report-page">
        <div class="container-fluid">
            <h1 class="kv-title" v-if="!is('noEmailsLifetime') && !is('noTrackingLifetime')">{{ t('emailReport-title') }}</h1>

            <div style="margin-bottom: 24px;" v-if="!is('noEmailsLifetime') && !is('noTrackingLifetime')">
                <KvFilter
                    :defaultSelected="['period']"
                    v-show="ready"
                    ref="filterBar"
                    @changed="onFilterChanged"
                    :has-branch-filter="canFilterPerBranch"
                    :filters="extraFilters"
                />
            </div>

            <loader v-if="is('loading')"></loader>

            <div v-if="is('success')">
                <!-- Stats -->
                <div class="stats-row">
                            <div class="stat-item">
                                <div class="icon">
                                    <Icon icon-class="ui-mail-02" size="13" />
                                </div>
                                <div class="info">
                                    <div class="number">
                                        {{ formatNumber(report.stats.total_sent) }}
                                    </div>
                                    <div class="label">{{ t('emailReport-stats-total_sent') }}</div>
                                </div>
                            </div>
                            <div class="stat-item">
                                <div class="icon">
                                    <Icon icon-class="ui-check" size="13" />
                                </div>
                                <div class="info">
                                    <div class="number">
                                        {{ formatNumber(report.stats.delivered) }}
                                    </div>
                                    <div class="label">
                                        {{ t('emailReport-stats-delivered') }}
                                        <span class="percentage">
                                            ({{ report.stats.delivery_rate }}%)
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div class="stat-item">
                                <div class="icon">
                                    <Icon icon-class="ui-eye" size="13" />
                                </div>
                                <div class="info">
                                    <div class="number">
                                        {{ formatNumber(report.stats.opened) }}
                                    </div>
                                    <div class="label">
                                        {{ t('emailReport-stats-opened') }}
                                        <span class="percentage">
                                            ({{ report.stats.open_rate }}%)
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div class="stat-item">
                                <div class="icon">
                                    <Icon icon-class="ui-cursor-click-01" size="13" />
                                </div>
                                <div class="info">
                                    <div class="number">
                                        {{ formatNumber(report.stats.clicked) }}
                                    </div>
                                    <div class="label">
                                        {{ t('emailReport-stats-clicked') }}
                                        <span class="percentage">
                                            ({{ report.stats.click_rate }}%)
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div class="stat-item">
                                <div class="icon">
                                    <Icon icon-class="ui-alert-circle" size="13" />
                                </div>
                                <div class="info">
                                    <div class="number">
                                        {{ formatNumber(report.stats.failed) }}
                                    </div>
                                    <div class="label">
                                        {{ t('emailReport-stats-failed') }}
                                        <span class="percentage">
                                            ({{ report.stats.bounce_rate }}%)
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Conversion Stats -->
                        <div class="stats-row conversion-stats-row">
                            <div class="stat-item">
                                <div class="icon">
                                    <Icon icon-class="ui-shopping-cart-03" size="13" />
                                </div>
                                <div class="info">
                                    <div class="number">
                                        {{ formatNumber(report.stats.total_orders) }}
                                    </div>
                                    <div class="label">{{ t('emailReport-stats-total_orders') }}</div>
                                </div>
                            </div>
                            <div class="stat-item">
                                <div class="icon">
                                    <Icon icon-class="ui-refresh-cw-02" size="13" />
                                </div>
                                <div class="info">
                                    <div class="number">
                                        {{ formatNumber(report.stats.total_reactivated_orders) }}
                                    </div>
                                    <div class="label">{{ t('emailReport-stats-total_reactivated_orders') }}</div>
                                </div>
                            </div>
                            <div class="stat-item">
                                <div class="icon">
                                    <Icon icon-class="ui-bank-note-01" size="13" />
                                </div>
                                <div class="info">
                                    <div class="number">
                                        {{ formatCurrency(report.stats.total_revenue) }}
                                    </div>
                                    <div class="label">{{ t('emailReport-stats-total_revenue') }}</div>
                                </div>
                            </div>
                        </div>

                        <!-- Time Series Chart -->
                        <div v-if="report.periodInDays > 1" class="chart-section mt-8">
                            <div class="chart-holder">
                                <multi-smooth-line-chart
                                    :chart-data="timeSeriesChartData"
                                    :style="chartStyles"
                                    :height="430"
                                    ref="timeSeriesChart"
                                />
                            </div>
                        </div>

                        <!-- Automation Breakdown -->
                        <div v-if="report.byAutomation && report.byAutomation.length > 0" class="mb-4">
                            <div class="table-header-actions">
                                <h2 class="section-title">{{ t('emailReport-table-title') }}</h2>
                                <KvButton
                                    variant="outlined"
                                    prepend-icon="ui-download-01"
                                    :loading="exporting"
                                    @click="exportToCsv"
                                >
                                    {{ t('emailReport-table-export_csv') }}
                                </KvButton>
                            </div>
                            <div class="table-wrapper">
                                <div class="table-responsive">
                                    <table class="table">
                                    <thead>
                                        <tr>
                                            <th>{{ t('emailReport-table-automation') }}</th>
                                            <th>{{ t('emailReport-table-sent') }}</th>
                                            <th>{{ t('emailReport-table-delivered') }}</th>
                                            <th>{{ t('emailReport-table-opened') }}</th>
                                            <th>{{ t('emailReport-table-clicked') }}</th>
                                            <th>{{ t('emailReport-table-replies') }}</th>
                                            <th>{{ t('emailReport-table-open_rate') }}</th>
                                            <th>{{ t('emailReport-table-click_rate') }}</th>
                                            <th>{{ t('emailReport-table-orders_count') }}</th>
                                            <th>{{ t('emailReport-table-reactivated_orders_count') }}</th>
                                            <th>{{ t('emailReport-table-revenue') }}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="automation in report.byAutomation" :key="automation.automation_id">
                                            <td>{{ automation.automation_name }}</td>
                                            <td>{{ formatNumber(automation.total_sent) }}</td>
                                            <td>{{ formatNumber(automation.delivered) }}</td>
                                            <td>{{ formatNumber(automation.opened) }}</td>
                                            <td>{{ formatNumber(automation.clicked) }}</td>
                                            <td>{{ formatNumber(automation.replied) }}</td>
                                            <td>
                                                {{ automation.total_sent > 0
                                                    ? ((automation.opened / automation.total_sent) * 100).toFixed(2)
                                                    : '0.00' }}%
                                            </td>
                                            <td>
                                                {{ automation.total_sent > 0
                                                    ? ((automation.clicked / automation.total_sent) * 100).toFixed(2)
                                                    : '0.00' }}%
                                            </td>
                                            <td>{{ formatNumber(automation.orders_count) }}</td>
                                            <td>{{ formatNumber(automation.reactivated_orders_count) }}</td>
                                            <td>{{ formatCurrency(automation.revenue) }}</td>
                                        </tr>
                                    </tbody>
                                    </table>
                                </div>
                            </div>
                            <kv-pagination
                                v-if="report.automationsPagination && report.automationsPagination.total > 0"
                                :total="report.automationsPagination.total"
                                :current-page="report.automationsPagination.page"
                                :items-per-page="report.automationsPagination.itemsPerPage"
                                :show-items-per-page="true"
                                :per-page-options="[10, 20, 50]"
                                @page-change="onPageChange"
                                @items-per-page-change="onItemsPerPageChange"
                            />
                        </div>
                    </div>

            <!-- Empty State: No emails sent in lifetime -->
            <div v-if="is('noEmailsLifetime')" class="empty-state">
                <div class="empty-state-content">
                    <p class="empty-state-message">
                        {{ t('emailReport-empty-no_emails_message') }}
                    </p>
                    <router-link to="/automations/new" class="empty-state-button">
                        {{ t('emailReport-empty-create_automation') }}
                    </router-link>
                </div>
            </div>

            <!-- Empty State: No tracking enabled in lifetime -->
            <div v-if="is('noTrackingLifetime')" class="empty-state">
                <div class="empty-state-content">
                    <p class="empty-state-message">
                        {{ t('emailReport-empty-no_tracking_message') }}
                    </p>
                </div>
            </div>

            <!-- Empty State: No emails in period (but has emails in other periods) -->
            <div v-if="is('noEmailsPeriod')">
                <div class="text-center py-4">
                    <p class="text-secondary">
                        {{ t('emailReport-empty-no_data_period') }}
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<style>
.notifications-email-report {
   background-color: #f3f4f6;
}
</style>
<style lang="scss" scoped>
.email-report-page {
    background-color: #f3f4f6;
    min-height: 100vh;
    padding: 0 20px;

    .container-fluid {
        background-color: #fff;
        border-radius: 16px;
        padding: 20px;
    }

    .kv-title {
        font-size: 24px;
        font-weight: 400;
        color: #1f2a37;
        margin: 10px 0 20px 0;
        text-transform: none;
    }

    .section-title {
        font-size: 18px;
        font-weight: 600;
        color: #111927;
        margin-bottom: 16px;
    }

    .chart-section {
        margin-top: 30px;
        margin-bottom: 20px; // less space below chart
    }

    .stats-row {
        display: flex;
        gap: 16px;
        margin-bottom: 24px;
        flex-wrap: wrap;

        @media (max-width: 768px) {
            flex-direction: column;
        }
    }

    .stat-item {
        position: relative;
        padding: 20px;
        background-color: #fff;
        border-radius: 8px;
        border: 1px solid #E5E7EB;
        flex: 1;
        min-width: 0;

        .icon {
            font-size: 50px;
            color: var(--secondary);
            position: absolute;
            width: 75px;
            height: 100%;
            padding-top: 5px;
            left: 0;
            top: 0;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .icon + .info {
            padding-left: 60px;
        }

        .number {
            font-weight: 600;
            font-size: 21px;
            color: #111927;
        }

        .label {
            font-size: 13px;
            font-weight: 400;
            color: #9DA4AE;
        }

        @media (max-width: 768px) {
            flex: 1 1 100%;
        }
    }

    .chart-holder {
        min-height: 284px;
        position: relative;
    }

    .percentage {
        font-size: 0.85em;
        color: #9DA4AE;
        font-weight: normal;
    }

    .table-wrapper {
        position: relative;
        overflow: hidden;
        border-radius: 12px;

        &:before {
            content: "";
            position: absolute;
            inset: 0;
            border: 1px solid #E5E7EB;
            pointer-events: none;
            border-radius: 12px;
        }
    }

    .empty-state {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 400px;
        padding: 40px 20px;

        .empty-state-content {
            text-align: center;
            max-width: 500px;

            .empty-state-message {
                font-size: 16px;
                color: #4F4F4F;
                margin-bottom: 24px;
                line-height: 1.5;
            }

            .empty-state-button {
                display: inline-block;
                padding: 12px 24px;
                background-color: var(--primary);
                color: #fff;
                border-radius: 8px;
                text-decoration: none;
                font-weight: 500;
                font-size: 14px;
                transition: background-color 0.2s;

                &:hover {
                    background-color: var(--primary-dark, #1a7a3a);
                }
            }
        }
    }

    .conversion-stats-row {
        margin-top: -8px;

        .stat-item {
            .icon {
                color: var(--primary);
            }
        }
    }

    .table-header-actions {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;

        .section-title {
            margin-bottom: 0;
        }
    }

    .table-responsive {
        border-radius: 16px;
        background: #FBFBFB;
        overflow-x: auto;

        /* width */
        &::-webkit-scrollbar {
            height: 12px;
        }

        /* Track */
        &::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 10px;
        }

        /* Handle */
        &::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 10px;
            border: 2px solid #f1f1f1;
        }

        /* Handle on hover */
        &::-webkit-scrollbar-thumb:hover {
            background: #555;
        }
    }

    .table {
        margin-bottom: 0;

        thead {
            background: var(--primary);

            th {
                vertical-align: middle;
                color: #fff;
                font-size: 13px;
                line-height: 1;
                height: 50px;
                text-align: start;
                padding: 0 12px;
            }
        }

        tbody {
            tr {
                background: #FBFBFB;

                &:hover {
                    background: #e5e7eb;

                    td {
                        background-color: #e5e7eb;
                    }
                }

                td {
                    background-color: #FBFBFB;
                    height: 50px;
                    vertical-align: middle;
                    transition: all ease-in-out 0.3s;
                    color: #4F4F4F;
                    font-size: 13px;
                    text-align: start;
                    padding: 0 12px;
                }
            }
        }
    }
}
</style>

<script>
import keviApi from "../keviApi";
import KvFilter from "../components/reports/kv-filter-bar";
import MultiSmoothLineChart from "../components/charts/multi-smooth-line-chart";
import Icon from "../components/icon/icon";
import KvPagination from "../components/ui/kv-pagination.vue";
import KvButton from "../components/form/kv-button.vue";
import CurrencyMixin from "../mixins/currency-mixin";
import saveData from "../helpers/saveData";
import Papa from "papaparse";
import { mapState } from "vuex";

export default {
    mixins: [CurrencyMixin],
    components: {
        KvFilter,
        MultiSmoothLineChart,
        Icon,
        KvPagination,
        KvButton,
    },
    computed: {
        ...mapState("placeDetails", ["users", "roles", "branches"]),
        user() {
            return this.$store.state.user;
        },
        canFilterPerBranch() {
            return !!(
                this.user &&
                this.user.permissions &&
                this.user.permissions["reports"] &&
                this.user.permissions["reports"]["options"] &&
                this.user.permissions["reports"]["options"]["global_access"]
            );
        },
        timeSeriesChartData() {
            if (!this.report.timeSeries || this.report.timeSeries.length === 0) {
                return {
                    labels: [],
                    datasets: [],
                };
            }

            // Check if period is >= 30 days for monthly aggregation
            const isMonthly = this.report.periodInDays >= 30;

            const parseReportDate = (dateStr) => {
                if (!dateStr) return null;
                // Backend returns DATE(...) or DATE_FORMAT(..., '%Y-%m-01') => "YYYY-MM-DD"
                if (typeof dateStr === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                    return new Date(`${dateStr}T00:00:00.000Z`);
                }
                return new Date(dateStr);
            };

            const localeMap = { ptBr: "pt-BR", en: "en-US", es: "es-ES" };
            const locale = localeMap[this.$root.lang] || "pt-BR";

            const labels = this.report.timeSeries.map((item) => {
                const date = parseReportDate(item.date);
                if (!date || isNaN(date.getTime())) {
                    return "";
                }
                if (isMonthly) {
                    // Format as month/year for monthly aggregation
                    return date.toLocaleDateString(locale, {
                        month: "short",
                        year: "numeric",
                        timeZone: "UTC",
                    });
                } else {
                    // Format as day/month for daily aggregation
                    return date.toLocaleDateString(locale, {
                        day: "2-digit",
                        month: "2-digit",
                        timeZone: "UTC",
                    });
                }
            });

            return {
                labels: labels,
                datasets: [
                    {
                        label: this.t('emailReport-chart-sent'),
                        data: this.report.timeSeries.map((item) => item.total_sent),
                        borderColor: "#2970FF",
                        backgroundColor: "rgba(41, 112, 255, 0.1)",
                        borderWidth: 2,
                        fill: false,
                        tension: 0.4,
                        pointRadius: 0,
                        pointHoverRadius: 5,
                        pointBackgroundColor: "#2970FF",
                        pointBorderColor: "#FFFFFF",
                        pointBorderWidth: 2,
                    },
                    {
                        label: this.t('emailReport-chart-delivered'),
                        data: this.report.timeSeries.map((item) => item.delivered),
                        borderColor: "#17B26A",
                        backgroundColor: "rgba(23, 178, 106, 0.1)",
                        borderWidth: 2,
                        fill: false,
                        tension: 0.4,
                        pointRadius: 0,
                        pointHoverRadius: 5,
                        pointBackgroundColor: "#17B26A",
                        pointBorderColor: "#FFFFFF",
                        pointBorderWidth: 2,
                    },
                    {
                        label: this.t('emailReport-chart-opened'),
                        data: this.report.timeSeries.map((item) => item.opened),
                        borderColor: "#FDA712",
                        backgroundColor: "rgba(253, 167, 18, 0.1)",
                        borderWidth: 2,
                        fill: false,
                        tension: 0.4,
                        pointRadius: 0,
                        pointHoverRadius: 5,
                        pointBackgroundColor: "#FDA712",
                        pointBorderColor: "#FFFFFF",
                        pointBorderWidth: 2,
                    },
                    {
                        label: this.t('emailReport-chart-clicked'),
                        data: this.report.timeSeries.map((item) => item.clicked),
                        borderColor: "#F97066",
                        backgroundColor: "rgba(249, 112, 102, 0.1)",
                        borderWidth: 2,
                        fill: false,
                        tension: 0.4,
                        pointRadius: 0,
                        pointHoverRadius: 5,
                        pointBackgroundColor: "#F97066",
                        pointBorderColor: "#FFFFFF",
                        pointBorderWidth: 2,
                    },
                ],
            };
        },
        chartStyles() {
            return {
                position: "relative",
                height: "350px",
            };
        },
    },
    data: () => ({
        filters: {},
        extraFilters: [],
        status: "loading",
        ready: false,
        exporting: false,
        report: {
            stats: {
                total_sent: 0,
                sent: 0,
                failed: 0,
                delivered: 0,
                opened: 0,
                replied: 0,
                clicked: 0,
                delivery_rate: 0,
                open_rate: 0,
                click_rate: 0,
                reply_rate: 0,
                bounce_rate: 0,
                total_orders: 0,
                total_reactivated_orders: 0,
                total_revenue: 0,
            },
            timeSeries: [],
            byAutomation: [],
            automationsPagination: {
                total: 0,
                page: 1,
                itemsPerPage: 20,
                totalPages: 0,
            },
            periodInDays: 0,
        },
        page: 1,
        itemsPerPage: 20,
    }),
    created() {
        this.page = parseInt(this.$route.query.page) || 1;
        this.itemsPerPage = parseInt(this.$route.query.itemsPerPage) || 20;
    },
    async mounted() {
        document.getElementsByTagName("title")[0].innerText =
            this.t('emailReport-page_title');

        // Add automation filter
        await this.loadAutomations();

        // Add status filter
        this.extraFilters.push({
            component: "checkbox",
            label: this.t('emailReport-filters-status_label'),
            name: "status",
            options: {
                options: [
                    { value: "accepted", label: this.t('emailReport-filters-status_accepted') },
                    { value: "scheduled", label: this.t('emailReport-filters-status_scheduled') },
                    { value: "sent", label: this.t('emailReport-filters-status_sent') },
                    { value: "failed", label: this.t('emailReport-filters-status_failed') },
                ],
                icon: "ui-check-circle",
            },
            removable: true,
        });

        this.ready = true;
    },
    methods: {
        t: function (term, replaces) {
            return this.$root.t(term, replaces);
        },
        is(status) {
            return this.status === status;
        },
        updateRouteQuery() {
            const query = {
                ...this.$route.query,
                page: this.page,
                itemsPerPage: this.itemsPerPage,
            };

            if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) {
                this.$router.replace({ query }).catch(() => {});
            }
        },
        onFilterChanged(filters) {
            if (Object.keys(this.filters).length > 0 && JSON.stringify(filters) !== JSON.stringify(this.filters)) {
                this.page = 1;
                this.updateRouteQuery();
            }
            this.loadData(filters);
        },
        onPageChange(newPage) {
            this.page = newPage;
            this.updateRouteQuery();
            this.loadData();
        },
        onItemsPerPageChange(newLimit) {
            this.itemsPerPage = newLimit;
            this.page = 1;
            this.updateRouteQuery();
            this.loadData();
        },
        async loadAutomations() {
            try {
                const options = keviApi.getOptions(
                    "notifications/automations",
                    "GET"
                );
                const response = await this.$http(options);
                if (response.body && response.body.automations) {
                    const automations = response.body.automations;
                    if (automations.length > 0) {
                        // Remove duplicates by id
                        const uniqueAutomations = automations.reduce((acc, automation) => {
                            if (!acc.find(a => a.id === automation.id)) {
                                acc.push(automation);
                            }
                            return acc;
                        }, []);

                        this.extraFilters.push({
                            component: "checkbox",
                            label: this.t('emailReport-filters-automation_label'),
                            name: "automation_id",
                            options: {
                                options: uniqueAutomations.map((automation) => ({
                                    value: automation.id,
                                    label: automation.title,
                                })) ,
                                icon: "ui-route",
                            },
                            removable: true,
                        });
                    }
                }
            } catch (error) {
                console.error("Error loading automations:", error);
            }
        },
        async loadData(filters) {
            if (filters) {
                this.filters = filters;
            }
            this.status = "loading";

            try {
                const options = keviApi.getOptions(
                    "notifications/email-report",
                    "GET",
                    {
                        filters: this.filters,
                        page: this.page,
                        itemsPerPage: this.itemsPerPage,
                    }
                );
                const response = await this.$http(options);

                if (response.status === 200 && response.body) {
                    this.report = response.body;

                    // Use empty state from backend
                    if (this.report.emptyState === 'no_emails_lifetime') {
                        this.status = "noEmailsLifetime";
                    } else if (this.report.emptyState === 'no_tracking_lifetime') {
                        this.status = "noTrackingLifetime";
                    } else if (this.report.emptyState === 'no_emails_period') {
                        this.status = "noEmailsPeriod";
                    } else if (this.report.stats.total_sent > 0) {
                        this.status = "success";
                    } else {
                        this.status = "empty";
                    }
                } else {
                    this.status = "empty";
                }
            } catch (error) {
                console.error("Error loading email report:", error);
                this.status = "empty";
            }
        },
        formatNumber(number) {
            if (!number) {
                return 0;
            }
            return parseFloat(number).toLocaleString("pt-BR");
        },
        async exportToCsv() {
            if (!this.report.byAutomation || this.report.byAutomation.length === 0) {
                return;
            }
            this.exporting = true;
            try {
                let automations = this.report.byAutomation;
                if (
                    this.report.automationsPagination &&
                    (this.report.automationsPagination.totalPages > 1 || this.report.automationsPagination.total > automations.length)
                ) {
                    const options = keviApi.getOptions(
                        "notifications/email-report",
                        "GET",
                        {
                            filters: this.filters,
                            all: true,
                        }
                    );
                    const res = await this.$http(options);
                    if (res.body && res.body.byAutomation) {
                        automations = res.body.byAutomation;
                    }
                }

                const data = automations.map((auto) => {
                    const openRate = auto.total_sent > 0
                        ? ((auto.opened / auto.total_sent) * 100).toFixed(2) + "%"
                        : "0.00%";
                    const clickRate = auto.total_sent > 0
                        ? ((auto.clicked / auto.total_sent) * 100).toFixed(2) + "%"
                        : "0.00%";

                    return {
                        [this.t('emailReport-table-automation')]: auto.automation_name,
                        [this.t('emailReport-table-sent')]: auto.total_sent,
                        [this.t('emailReport-table-delivered')]: auto.delivered,
                        [this.t('emailReport-table-opened')]: auto.opened,
                        [this.t('emailReport-table-clicked')]: auto.clicked,
                        [this.t('emailReport-table-replies')]: auto.replied,
                        [this.t('emailReport-table-open_rate')]: openRate,
                        [this.t('emailReport-table-click_rate')]: clickRate,
                        [this.t('emailReport-table-orders_count')]: auto.orders_count ?? 0,
                        [this.t('emailReport-table-reactivated_orders_count')]: auto.reactivated_orders_count ?? 0,
                        [this.t('emailReport-table-revenue')]: this.formatCurrency(auto.revenue ?? 0),
                    };
                });

                const csv = "\uFEFF" + Papa.unparse(data);
                const startDate = this.report.startDate ? this.report.startDate.split(" ")[0] : "";
                const endDate = this.report.endDate ? this.report.endDate.split(" ")[0] : "";
                const baseFilename = this.t('emailReport-table-export_filename') || "email-automations-report";
                const filename = `${baseFilename}${startDate ? `-${startDate}` : ""}${endDate ? `-${endDate}` : ""}.csv`;
                saveData(csv, filename);
            } catch (err) {
                console.error("Error exporting CSV:", err);
            } finally {
                this.exporting = false;
            }
        },
    },
};
</script>

